import { SubscriptionActionTypes, SubscriptionStore } from '@/store/owner/subscription'
import { defineComponent } from 'vue'
import { mapActions, mapState } from 'vuex'
import { ScaProductPlan } from '~/types/types'

const PLAN_UPGRADES: Record<string, string[]> = {
  'plan-starter-monthly': ['plan-premium-monthly'],
  'plan-starter-annual': ['plan-premium-annual'],
  'plan-premium-monthly': [],
  'plan-premium-annual': []
}

const PLAN_DOWNGRADES: Record<string, string[]> = {
  'plan-starter-monthly': [],
  'plan-starter-annual': [],
  'plan-premium-monthly': ['plan-starter-monthly'],
  'plan-premium-annual': ['plan-starter-annual']
}

export interface Plan {
  planName: string
  annualAmount?: number
  monthlyAmount?: number
  planSlugMonthly: string
  planSlugAnnual: string
  isPrimary?: boolean
  description?: string
  buttonLabel?: string
  routeTo?: string
  features?: string[]
}

export interface SCAPlan {
  plan: ScaProductPlan
  planName: string
  annualAmount?: number
  monthlyAmount?: number
  features?: string[]
}

const SCA_PLANS: Record<ScaProductPlan, SCAPlan> = {
  [ScaProductPlan.PlanFree]: {
    planName: 'Free',
    plan: ScaProductPlan.PlanFree,
    features: []
  },
  [ScaProductPlan.PlanStarter]: {
    plan: ScaProductPlan.PlanStarter,
    planName: 'SCA Premium',
    annualAmount: 80,
    monthlyAmount: 10,
    features: [
      'Pay-as-you-go, per target',
      'Unlimited analysis runs ',
      'Unlimited remediation with Autofix™ AI',
      'Standard support included'
    ]
  }
}

const PLANS: Record<string, Plan> = {
  starter: {
    planName: 'Code Analysis Starter',
    planSlugMonthly: 'plan-starter-monthly',
    planSlugAnnual: 'plan-starter-annual',
    description: 'For open-source maintainers and small teams',
    buttonLabel: 'Upgrade to Starter',
    monthlyAmount: 10,
    annualAmount: 8,
    features: [
      'All features in Free',
      'Unlimited team members',
      'Unlimited public and private repositories'
    ]
  },
  premium: {
    planName: 'Code Analysis Business',
    planSlugMonthly: 'plan-premium-monthly',
    planSlugAnnual: 'plan-premium-annual',
    description: 'For open-source maintainers and small teams',
    buttonLabel: 'Upgrade to Business',
    monthlyAmount: 30,
    annualAmount: 24,
    isPrimary: true,
    features: [
      'All features in Starter',
      'Access to Core, Community, and Custom analyzers',
      'Code Formatters, Autofix, Autopilot unlimited',
      'RBAC',
      'Audit Logs',
      'Org-level defaults configuration',
      '3 years retention of reporting data',
      'Priority support',
      'API and webhooks'
    ]
  },
  business: {
    planName: 'Code Analysis Business',
    planSlugMonthly: 'plan-business-monthly',
    planSlugAnnual: 'plan-business-annual',
    description: 'For open-source maintainers and small teams',
    buttonLabel: 'Upgrade to Business',
    monthlyAmount: 30,
    annualAmount: 24,
    isPrimary: true,
    features: [
      'All features in Starter',
      'Access to Core, Community, and Custom analyzers',
      'Code Formatters, Autofix, Autopilot unlimited',
      'RBAC',
      'Audit Logs',
      'Org-level defaults configuration',
      '3 years retention of reporting data',
      'Priority support',
      'API and webhooks'
    ]
  },
  enterprise: {
    planName: 'Enterprise',
    planSlugMonthly: '',
    planSlugAnnual: '',
    description: 'For large teams with on-premise deployments.',
    buttonLabel: 'Request a Demo',
    routeTo: 'https://deepsource.com/schedule-demo',
    features: [
      'All features in Business',
      'Pre-defined number of team members',
      'Unlimited public and private repos'
    ]
  }
}

export default defineComponent({
  name: 'SubscriptionMixin',

  computed: {
    ...(mapState<SubscriptionStore>('owner/subscription', {
      selectedPlan: (state: SubscriptionStore) => state.selectedPlan,
      billingCycle: (state: SubscriptionStore) => state.billingCycle,
      seats: (state: SubscriptionStore) => state.seats
    }) as {
      selectedPlan: () => SubscriptionStore['selectedPlan']
      billingCycle: () => SubscriptionStore['billingCycle']
      seats: () => SubscriptionStore['seats']
    }),
    planDetails(): Record<string, Plan> {
      return PLANS
    },
    scaPlans(): Record<string, SCAPlan> {
      return SCA_PLANS
    },
    planUpgradeOptions(): Record<string, string[]> {
      return PLAN_UPGRADES
    },
    planDowngradeOptions(): Record<string, string[]> {
      return PLAN_DOWNGRADES
    }
  },

  methods: {
    ...mapActions('owner/subscription', {
      setPlan: SubscriptionActionTypes.SET_PLAN,
      resetPlan: SubscriptionActionTypes.RESET_PLAN
    })
  }
})
